import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';

import submitIcon from '../../../assets/images/SVG/submit_icon.svg';

const SubmitProgressModal = ({
  isOpen,
  onClose,
  onSubmitConfirm,
  isLoading,
  questions = [],
  answers = {},
  onNavigateToQuestion
}) => {
  // Count answered vs unanswered
  const totalQuestions = questions.length;
  let answeredCount = 0;
  
  questions.forEach((q, idx) => {
    const ans = answers[idx];
    const hasAns = ans !== undefined && ans !== null && ans !== '' && (typeof ans !== 'object' || Object.values(ans).some(val => val !== undefined && val !== ''));
    if (hasAns) {
      answeredCount++;
    }
  });

  const unansweredCount = totalQuestions - answeredCount;

  return (
    <Dialog
      open={isOpen}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose(event, reason);
        }
      }}
      disableScrollLock
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(8px)',
        },
        '& .MuiPaper-root': {
          borderRadius: '24px !important',
          width: '100%',
          maxWidth: '500px',
          margin: '16px',
          boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.8)',
          fontFamily: "'Poppins', sans-serif",
          overflow: 'visible',
          position: 'relative',
          p: { xs: 3, sm: 4 },
          pt: { xs: 5, sm: 6 }
        }
      }}
    >
      {/* Top Right Close Button */}
      <IconButton
        onClick={onClose}
        disabled={isLoading}
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 20,
          top: 20,
          color: '#ef4444',
          bgcolor: '#ffffff',
          border: '1.5px solid #fee2e2',
          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.08)',
          transition: 'all 0.25s',
          width: 36,
          height: 36,
          '&:hover': {
            color: '#dc2626',
            backgroundColor: '#fee2e2',
            transform: 'rotate(90deg)'
          }
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </IconButton>

      <DialogContent sx={{ p: 0, textAlign: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Centered Submit Icon SVG */}
          <Box
            component="img"
            src={submitIcon}
            alt="Submit Exam"
            sx={{
              width: '80px',
              height: '80px',
              mb: 2.5,
              filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.05))'
            }}
          />

          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 750,
              color: '#1e293b',
              mb: 1.5,
              fontSize: { xs: '20px', sm: '22px' },
              fontFamily: "'Poppins', sans-serif"
            }}
          >
            Ready to Submit?
          </Typography>

          {/* Description */}
          <Typography
            sx={{
              fontSize: '13px',
              color: '#64748b',
              fontFamily: "'Poppins', sans-serif",
              lineHeight: 1.5,
              mb: 3,
              px: 1
            }}
          >
            Please review your question progress below. Clicking on any number allows you to jump back and change your answer.
          </Typography>

          {/* Progress stats summary */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 3,
              width: '100%',
              justifyContent: 'center'
            }}
          >
            <Box
              sx={{
                bgcolor: '#ecfdf5',
                color: '#10b981',
                borderRadius: '12px',
                px: 2,
                py: 1,
                minWidth: '100px',
                textAlign: 'center'
              }}
            >
              <Typography sx={{ fontSize: '18px', fontWeight: 750, fontFamily: 'Poppins' }}>
                {answeredCount}
              </Typography>
              <Typography sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Poppins', color: '#059669' }}>
                Answered
              </Typography>
            </Box>
            <Box
              sx={{
                bgcolor: '#fffbeb',
                color: '#d97706',
                borderRadius: '12px',
                px: 2,
                py: 1,
                minWidth: '100px',
                textAlign: 'center'
              }}
            >
              <Typography sx={{ fontSize: '18px', fontWeight: 750, fontFamily: 'Poppins' }}>
                {unansweredCount}
              </Typography>
              <Typography sx={{ fontSize: '11px', fontWeight: 600, fontFamily: 'Poppins', color: '#b45309' }}>
                Unanswered
              </Typography>
            </Box>
          </Box>

          {/* Question Grid Status Navigator Map */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(8, 1fr)',
              gap: 1,
              width: '100%',
              maxHeight: '200px',
              overflowY: 'auto',
              p: 0.5,
              mb: 4,
              border: '1.5px solid #f1f5f9',
              borderRadius: '16px',
              bgcolor: '#f8fafc',
              '&::-webkit-scrollbar': {
                width: '6px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#cbd5e1',
                borderRadius: '3px'
              }
            }}
          >
            {questions.map((q, idx) => {
              const ans = answers[idx];
              const hasAns = ans !== undefined && ans !== null && ans !== '' && (typeof ans !== 'object' || Object.values(ans).some(val => val !== undefined && val !== ''));

              const cellBg = hasAns ? '#ecfdf5' : '#ffffff';
              const cellColor = hasAns ? '#10b981' : '#94a3b8';
              const cellBorder = hasAns ? '1.5px solid #10b981' : '1.5px dashed #cbd5e1';

              return (
                <Box
                  key={q.id || idx}
                  onClick={() => {
                    if (onNavigateToQuestion && !isLoading) {
                      onNavigateToQuestion(idx);
                      onClose();
                    }
                  }}
                  sx={{
                    aspectRatio: '1',
                    borderRadius: '8px',
                    border: cellBorder,
                    bgcolor: cellBg,
                    color: cellColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 700,
                    fontFamily: 'Poppins',
                    cursor: onNavigateToQuestion ? 'pointer' : 'default',
                    transition: 'all 0.2s',
                    '&:hover': onNavigateToQuestion && !isLoading ? {
                      transform: 'scale(1.15)',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                      borderColor: hasAns ? '#059669' : '#94a3b8',
                      bgcolor: hasAns ? '#d1fae5' : '#f1f5f9'
                    } : {}
                  }}
                >
                  {String(idx + 1).padStart(2, '0')}
                </Box>
              );
            })}
          </Box>

          {/* Action buttons */}
          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            <Button
              onClick={onClose}
              disabled={isLoading}
              variant="outlined"
              sx={{
                flex: 1,
                borderRadius: '9999px',
                py: 1.5,
                fontSize: '15px',
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: "'Poppins', sans-serif",
                borderColor: '#64748b',
                color: '#64748b',
                borderWidth: '1.5px',
                '&:hover': {
                  borderColor: '#475569',
                  bgcolor: '#f8fafc',
                  borderWidth: '1.5px'
                }
              }}
            >
              Back to Exam
            </Button>
            
            <Button
              onClick={onSubmitConfirm}
              variant="contained"
              disabled={isLoading}
              sx={{
                flex: 1,
                background: 'linear-gradient(90deg, #FF8B4A 0%, #FF6244 100%)',
                color: '#ffffff',
                borderRadius: '9999px',
                py: 1.5,
                fontSize: '15px',
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: "'Poppins', sans-serif",
                '&:hover': {
                  background: 'linear-gradient(90deg, #ff9b5f 0%, #ff7357 100%)',
                  transform: 'translateY(-2px)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                },
                '&.Mui-disabled': {
                  background: '#cbd5e1',
                  color: '#94a3b8'
                },
                transition: 'all 0.25s ease'
              }}
            >
              {isLoading ? <CircularProgress size={24} sx={{ color: '#ffffff' }} /> : 'Submit Now'}
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SubmitProgressModal;
